import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Checkbox, Icon, Toggle } from '@/nova/components';

import type { BannerKitCatalog, KitCatalogSize, KitId } from '../../banner-kit-api';
import * as S from './BannerKitComposer.styles';

export interface KitPickerProps {
  catalog: BannerKitCatalog;
  kits: KitId[];
  /** Sizes switched off, by size id (one switch per size, shared by every kit that has it). */
  sizesOff: string[];
  disabled?: boolean;
  onChange: (next: { kits: KitId[]; sizes_off: string[] }) => void;
}

/** A kit's display name. One literal `t()` per id so the extractor harvests each; an unknown id keeps the
 *  catalogue's English label. */
export const kitLabel = (id: string, fallback: string, t: TFunction): string => {
  switch (id) {
    case 'social':
      return t('Social');
    case 'display':
      return t('Display & affiliates');
    default:
      return fallback;
  }
};

/** "300×250" (the multiplication sign, as ad specs write it). */
export const sizeLabel = (size: Pick<KitCatalogSize, 'w' | 'h'>): string => `${size.w}×${size.h}`;

/** A size's weight budget, in KB below 1 MB and MB above, marked approximate (an export lands near it, not on it). */
const WeightMeta = ({ kb }: { kb: number }): React.ReactElement =>
  kb >= 1024 ? (
    <S.SizeMeta text="~{{n}} MB" placeholders={{ n: Math.round((kb / 1024) * 10) / 10 }} />
  ) : (
    <S.SizeMeta text="~{{n}} KB" placeholders={{ n: kb }} />
  );

/**
 * The composer's kit list: one checkbox per kit (Social, Display & affiliates, from the served catalogue), each
 * expandable into its sizes with a per-size switch (a social size names the networks it fits). A size that several kits share has one switch (turning it off
 * removes it from the whole kit set), so the list shows the same state under each kit.
 */
export const KitPicker = ({
  catalog,
  kits,
  sizesOff,
  disabled = false,
  onChange,
}: KitPickerProps): React.ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState<ReadonlySet<string>>(() => new Set());

  const toggleKit = (id: KitId, on: boolean): void => {
    if (disabled) return;
    const next = on ? [...kits.filter((k) => k !== id), id] : kits.filter((k) => k !== id);
    // Keep the catalogue order so the document (and the cost chip) never depends on the click order.
    const order = catalog.kits.map((k) => k.id);
    onChange({ kits: next.sort((a, b) => order.indexOf(a) - order.indexOf(b)), sizes_off: sizesOff });
  };

  const toggleSize = (sizeId: string): void => {
    if (disabled) return;
    const off = sizesOff.includes(sizeId);
    onChange({ kits, sizes_off: off ? sizesOff.filter((s) => s !== sizeId) : [...sizesOff, sizeId] });
  };

  const toggleOpen = (id: string): void =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <S.KitList>
      {catalog.kits.map((kit) => {
        const on = kits.includes(kit.id);
        const expanded = open.has(kit.id);
        const name = kitLabel(kit.id, kit.label, t);
        const count = on ? kit.sizes.filter((s) => !sizesOff.includes(s.id)).length : kit.sizes.length;
        return (
          <S.KitRow key={kit.id}>
            <S.KitHead>
              <S.KitCheck>
                <Checkbox checked={on} onChange={(v) => toggleKit(kit.id, v)} label={name} disabled={disabled} />
              </S.KitCheck>
              <S.KitCount raw text={t('{{count}} sizes', { count })} />
              <S.ExpandBtn
                type="button"
                $open={expanded}
                aria-expanded={expanded}
                aria-label={
                  expanded ? t('Hide the {{kit}} sizes', { kit: name }) : t('Show the {{kit}} sizes', { kit: name })
                }
                onClick={() => toggleOpen(kit.id)}
              >
                <Icon name="chevron-down" size={16} />
              </S.ExpandBtn>
            </S.KitHead>
            {expanded ? (
              <S.SizeList>
                {kit.sizes.map((size) => {
                  const sizeOn = !sizesOff.includes(size.id);
                  const label = sizeLabel(size);
                  return (
                    <S.SizeRow key={size.id} $off={!on || !sizeOn}>
                      <S.SizeInfo>
                        <S.SizeName raw text={label} />
                        {size.networks?.length ? <S.SizeNetworks raw text={size.networks.join(' · ')} /> : null}
                      </S.SizeInfo>
                      <WeightMeta kb={size.max_kb} />
                      <Toggle
                        on={on && sizeOn}
                        label={t('Include {{size}}', { size: label })}
                        disabled={disabled || !on}
                        onToggle={() => toggleSize(size.id)}
                      />
                    </S.SizeRow>
                  );
                })}
              </S.SizeList>
            ) : null}
          </S.KitRow>
        );
      })}
    </S.KitList>
  );
};

export default KitPicker;
